import { Court, SubdivisionKind, SubdivisionLookupResult } from '@ee/common/models';

/**
 * The county → court and county → subdivision decisions shared by the v1 property step and the v2
 * address block. Each view still owns its form, its requests, and the rules that have deliberately
 * diverged (parking a cleared venue, which subdivision value a new county may keep, the county-list
 * race token). Those stay on the component. What is here is the part a disagreement in would be a bug.
 */

export interface CourtRecommendationInput {
  courtSelectionEnabled: boolean;
  hasCounty: boolean;
  countyId: string | undefined;
  /** `courts.length`, with a missing list as 0. The list itself stays with the caller. */
  courtCount: number;
  courtsCountyId: string | undefined;
  subdivisionsCountyId: string | undefined;
  hasInitialCounty: boolean;
  initialCountyId: string | undefined;
  /** The v2 block sets this once someone picks a court by hand. v1 has no such lock. */
  courtLockedByUser: boolean;
  subdivision: unknown;
  subdivisionKind: string | null | undefined;
  place: unknown;
}

export type CourtRecommendation =
  | { status: 'skip' }
  | { status: 'clear' }
  | { status: 'recommend'; countyId: string | undefined; subdivision: string; place: string | undefined };

/**
 * Whether to ask for a court, and with what. `skip` leaves the badge alone — an early return must
 * not wipe the explanation of a selection this call never re-made. `clear` drops the badge and
 * stops, which is the "no subdivision to recommend from" case. `recommend` is the request.
 */
export function courtRecommendation(input: CourtRecommendationInput): CourtRecommendation {
  if (!input.courtSelectionEnabled) return { status: 'skip' };
  if (!input.hasCounty || input.courtCount === 0) return { status: 'skip' };
  // A court list still belonging to the previous county must never be matched against this one.
  if (input.courtsCountyId !== input.countyId) return { status: 'skip' };
  // The subdivision configuration has not settled, so `place` cannot be decided yet.
  if (input.subdivisionsCountyId !== input.countyId) return { status: 'skip' };
  // Restoring the county the form opened with is not a new choice.
  if (input.hasInitialCounty && input.initialCountyId === input.countyId) return { status: 'skip' };
  if (input.courtLockedByUser) return { status: 'skip' };
  if (!input.subdivision) return { status: 'clear' };

  const place =
    input.subdivisionKind === 'PRECINCT'
      ? (input.place as string | null | undefined)?.trim() || undefined
      : undefined;
  return {
    status: 'recommend',
    countyId: input.countyId,
    subdivision: input.subdivision as string,
    place
  };
}

/** The loaded court the recommendation names, and why it was chosen. */
export function matchRecommendedCourt<T extends Pick<Court, 'id' | 'name' | 'supported_subdivisions'>>(
  courts: readonly T[],
  recommended: { id?: string; name?: string } | null | undefined,
  subdivision: string
): { court: T; source: 'subdivision' | 'default' } | null {
  if (!recommended) return null;
  const court = courts.find(candidate => candidate.id === recommended.id || candidate.name === recommended.name);
  if (!court) return null;
  const bySubdivision = court.supported_subdivisions?.some(
    name => name.toLowerCase() === subdivision.toLowerCase()
  );
  return { court, source: bySubdivision ? 'subdivision' : 'default' };
}

export type SubdivisionLookupDecision = 'skip' | 'skip-clear' | 'no-coordinates' | 'lookup';

/**
 * Whether this pass starts a boundary lookup. `skip-clear` also drops a stale hint. `skip` leaves
 * the hint: there is nothing to look up and nothing to warn about. Returning from the lookup is
 * the caller's signal to recommend a court anyway.
 */
export function subdivisionLookupDecision(input: {
  enabled: boolean;
  hasControl: boolean;
  visible: boolean;
  manual: boolean;
  keepStored: boolean;
  hasCoordinates: boolean;
}): SubdivisionLookupDecision {
  if (!input.enabled) return 'skip-clear';
  if (!input.hasControl) return 'skip';
  if (!input.visible) return 'skip-clear';
  if (input.manual) return 'skip';
  if (input.keepStored) return 'skip';
  if (!input.hasCoordinates) return 'no-coordinates';
  return 'lookup';
}

export type SubdivisionLookupOutcome =
  | { type: 'matched'; subdivision: string; kind: SubdivisionKind | null }
  | { type: 'cleared'; state: 'ambiguous' | 'none' };

/** What a lookup response writes. A manual choice that arrived while it was in flight is the caller's to ignore. */
export function subdivisionLookupOutcome(
  result: Pick<SubdivisionLookupResult, 'subdivision' | 'kind' | 'multiple_matches'> | null | undefined,
  fallbackKind: SubdivisionKind | null
): SubdivisionLookupOutcome {
  if (result?.subdivision) {
    return { type: 'matched', subdivision: result.subdivision, kind: result.kind ?? fallbackKind };
  }
  return { type: 'cleared', state: result?.multiple_matches ? 'ambiguous' : 'none' };
}
